import { Component, ViewEncapsulation, Input, Output, EventEmitter, ChangeDetectionStrategy, AfterViewInit, ElementRef, forwardRef, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { UtilitiesService } from '../../services/utilities.service';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, FormControl, ControlValueAccessor } from '@angular/forms';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { OrderMarketTypeEnum } from '../../enum/orders/market-type.enum';

// tslint:disable:variable-name

@Component({
    selector: 'tr-planned-checkout-input' ,
    templateUrl: './tr-planned-checkout-input.component.html',
    styleUrls: [ './tr-planned-checkout-input.component.scss' ],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TrPlannedCheckoutInputComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: TrPlannedCheckoutInputComponent,
            multi: true,
        },
    ],
})
export class TrPlannedCheckoutInputComponent implements AfterViewInit, ControlValueAccessor, OnInit {

    public startEditingIcon: string = 'icon_cross';
    public stopEditingIcon: string = 'icon_edit';

    @Output() public onEditingRequest: EventEmitter<any> = new EventEmitter();
    @Output() public onEditingEnd: EventEmitter<boolean> = new EventEmitter();
    @Output() public onValueChange: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('topLabelElement',  {static: true}) public topLabelElement: ElementRef;

    @Input() public set purchase(value: string) {
        this._purchase = value;
    }

    @Input() public set showBottomLabel(value: boolean) {
        this._showBottomLabel = value;
    }
    public get showBottomLabel(): boolean {
        return this._showBottomLabel;
    }

    @Input() public set plannedCheckout(value: boolean) {
        this._isPlannedCheckout = value;
    }
    public get plannedCheckout(): boolean {
        return this._isPlannedCheckout;
    }

    @Input() public set precision(value: number) {
        this._precision = value;
    }

    public get precision(): number {
        return this._precision;
    }
    @Input() public set preffix(value: string) {
        this._preffix = value;
    }
    public get preffix(): string {
        return this._preffix;
    }

    @Input() public set suffix(value: string) {
        this._suffix = value;
    }
    public get suffix(): string {
        return this._suffix;
    }

    @Input() public set floating(value: boolean) {
        this._floating = value;
    }
    public get floating(): boolean {
        return this._floating;
    }
    @Input() public set floatingControls(value: boolean) {
        this._floatingControls = value;
    }
    public get floatingControls(): boolean {
        return this._floatingControls;
    }
    public get floatingClass(): string {
        return this.floating ? 'floating' : 'no-floating';
    }
    @Input() public set planning(value: boolean) {
        this._planning = value;
    }
    @Input() public set executed(value: boolean) {
        this._executed = value;
    }

    /** habilitar os controles para iniciar/finalizar edição */
    @Input() public set enableEditingControls(value: boolean) {
        this._enableEditingControls = value;
    }
    public get enableEditingControls(): boolean {
        return this._enableEditingControls;
    }

    /** Identifica se o componente já foi desbloqueado para edição */
    public get previouslyUnlocked(): boolean {
        return this._previouslyUnlocked;
    }

    @Input() public set locked(value: boolean) {
        this._locked = value;
        if (!value) {
            this._previouslyUnlocked = true;
        }
    }
    public get locked(): boolean {
        return this._locked;
    }

    @Input() public set type(value: 'gain' | 'stop' | 'default') {
        this._type = value;
    }
    public get type(): 'gain' | 'stop' | 'default' {
        return this._type;
    }

    /** Valor do component */
    @Input() public set value(value: number) {
        this._value = value;
        this.propagateChange(this._value);
    }
    public get value(): number {
        return this._value;
    }

    @Input() public set symbol(symbol: any) {
        this._symbol = symbol;
    }
    public get symbol(): any {
        return this._symbol;
    }

    @Input() public set parentInstrument(parentInstrument: any) {
        this._parentInstrument = parentInstrument;
    }
    public get parentInstrument(): any {
        return this._parentInstrument;
    }

    /** Valor mínimo que o componente pode atingir */
    @Input() public set min(value: number) {
        this._min = value;
        (this._validationFunction as any) = this.createRangeValidator(this._min, this._max);
    }
    public get min(): number {
        return this._min;
    }

    /** Valor máximo que o componente pode atingir */
    @Input() public set max(value: number) {
        this._max = value;
        (this._validationFunction as any) = this.createRangeValidator(this._min, this._max);
    }

    public get max(): number {
        return this._max;
    }

    /** Valor base para os controles do componente */
    @Input() public set controlsBaseValue(value: number) {
        this.updateControlsValuesParameteres(value);
    }

    @Input() public set bottomLabel(value: string) {
        this._bottomLabel = value;
        // this._validationFunction = this.createRangeValidator(this._min, this._max);
    }

    public get bottomLabel(): string {
        // 'para comprar 1.000 ações a R$14,33';
        return this._bottomLabel;
    }

    /** Valores dos controles negativo */
    public get negativeMinValue(): number {
        return this._negativeMinValue;
    }

    public get negativeIntermediaryValue(): number {
        return this._negativeIntermediaryValue;
    }

    public get negativeMaxValue(): number {
        return this._negativeMaxValue;
    }

    /** Valores dos controles positivos */
    public get positiveMaxValue(): number {
        return this._positiveMaxValue;
    }

    public get positiveIntermediaryValue(): number {
        return this._positiveIntermediaryValue;
    }

    public get positiveMinValue(): number {
        return this._positiveMinValue;
    }

    /** Label superior da input */
    public get topLabel(): string {

        if (this._isPlannedCheckout) {
            let labelContent = 'ou <span class="popover-link">perder</span> aprox.';

            if (this._type === `default`) {
                labelContent = 'Quero investir aprox.';
            }

            if (this._type === `default` && this._executed) {
                labelContent = `Estou investindo`;
            }

            if (this._type === 'gain') {
                if (!this._planning) {
                    labelContent =  'Para <span class="popover-link">ganhar</span> aprox.';
                } else {
                    labelContent = 'Quero <span class="popover-link">ganhar</span> aprox.';
                }
            }
            return labelContent;
        } else {
            if (this._purchase) return 'Quero comprar';
            return 'Quero vender';
        }
    }

    public get topLabelTooltipContent(): string {
        let tooltipContent = `<div>
            <div class='pop-title'>{{title}}</div>
            <div class='pop-content'>{{content}}</div>
        </div>`;

        if (this._type === 'gain') {
            tooltipContent = tooltipContent.replace('{{title}}', 'Ganhar');
            tooltipContent = tooltipContent.replace('{{content}}', 'O investimento encerra automaticamente quando alcançar o lucro planejado.');
        } else {
            tooltipContent = tooltipContent.replace('{{title}}', 'Perder');
            tooltipContent = tooltipContent.replace(
                '{{content}}',
                'O investimento encerra automaticamente quando alcançar o prejuízo planejado.');
        }

        return tooltipContent;
    }

    private _value: number;
    private _min: number = -Infinity;
    private _max: number = Infinity;
    /** Valores dos controles negativos */
    private _negativeMinValue: number;
    private _negativeIntermediaryValue: number;
    private _negativeMaxValue: number;
    /** Valores dos controles prositivos */
    private _positiveMinValue: number;
    private _positiveIntermediaryValue: number;
    private _positiveMaxValue: number;
    /** Tipo do coomponente */
    private _type: 'gain' | 'stop' | 'default' = 'default';
    private _planning: boolean;
    private _executed: boolean;
    private _locked: boolean = true;
    private _topLabelTooltip: any;
    private _previouslyUnlocked: boolean = false;
    private _floating: boolean = false;
    private _floatingControls: boolean = false;
    private _enableEditingControls: boolean = true;
    private _validationFunction: (...args: any[]) => any;
    private _suffix: string = '';
    private _preffix: string = 'R$';
    private _precision: number = 2;
    private _showBottomLabel: boolean = true;
    private _purchase: string;
    private _bottomLabel: string;
    private _isPlannedCheckout: boolean;
    private _symbol: any;
    private _parentInstrument: any;

    constructor(
        private elementReference: ElementRef,
        private utilititiesService: UtilitiesService,
        private renderer: Renderer2,
    ) {}

    /** Toggle do estado de edição */
    public toggleEditing(event: Event) {

        if (!this.enableEditingControls) return;

        this.locked ? this.startEditing(event) : this.stopEditing(event);
    }

    public ngAfterViewInit() {
        const element: HTMLElement = this.topLabelElement.nativeElement.querySelector('.popover-link');
        if (element) {
            const tooltip = new TooltipDirective(new ElementRef(element));
            tooltip.tooltip = this.topLabelTooltipContent;
            tooltip.ngOnInit();
        }
    }

    /** Exibe a tooltip com o conteúdo descritivo do label superior */
    public showTopLabelTooltip(event) {

        if (!this._topLabelTooltip) {
            this._topLabelTooltip = this.utilititiesService.bindTooltipContent(event.target, this.topLabelTooltipContent);
        }

        this._topLabelTooltip.show();
    }

     /**
      * Retorna o stado atual do componente
      */
    public getCurrentComponentState(event: Event = null): {} {
        return {
            previousUnlocked: this._previouslyUnlocked,
            currentValue: this._value,
            originalEvent: event,
        };
    }

    /**
     * @name enableButton
     * @description Define se um botão deve ou não ser habilitado
     * @public
     *
     * @param {number} value - Valor do parâmetro do botão
     *
     * @returns {boolean}
     *
     */
    public enableButton(value: number) {

        /**
         * Os valores do cálculo não deve considerar casas decimais, os valores devem ser inteiros.
         */

        // Valor do checkout
        const checkoutValue = Math.ceil((this._value + value));

        this._min = this._min ? this._min : 0;
        this._max = this._max ? this._max : 0;

        // Valores min e máx do checkout
        const minValue = this._min === Infinity ? this._min : Number.parseInt((this._min.toFixed(2)), 10);
        const maxValue = this._max === Infinity ? this._max : Number.parseInt((this._max.toFixed(2)), 10);

        // Verifica pelo min ou máx de acordo com a ação de adição ou subtração de valor
        if (value < 0) {
            return (checkoutValue < minValue);
        } else {
            return (checkoutValue > maxValue);
        }

    }

     /** Trancar o componente para edição */
    public lock() {
        this._locked = true;
        this.onEditingEnd.emit(true);
    }
    /** Liberar o componente para edição */
    public unlock() {
        this._locked = false;
    }

    public ngOnInit() {
        this._validationFunction = this.createRangeValidator(this._min, this._max);
    }

    /** Valida o valor do controle(chamada pelo angular) */
    public validate(c: FormControl) {
        return this._validationFunction(c);
    }

    /** Form Integration Interface */
    public writeValue(value: any) {
        if (value) {
            this._value = value;
        }
    }

    public undoPreviouslyUnlocked() {
        this._locked = true;
        this._previouslyUnlocked = false;
    }

    /**
     * Propaga o valor para o form
     */
    public propagateChange = (_: any) => {
        //
    }

    public registerOnChange(fn) {
        this.propagateChange = fn;
    }

    /**
     * Disparado quando o controle e tocado
     */
    public registerOnTouched() {
        //
    }

    /**
     * Cria a função que valida o range definido para o componente.
     * @param minValue Limite inferior
     */
    public createRangeValidator(minValue, maxValue) {

        return function validateCounterRange(c: FormControl) {
            const err = {
                rangeError: {
                    given: c.value,
                    min: minValue,
                    max: maxValue,
                },
            };

            return (c.value < +minValue || c.value > maxValue) ? err : null;
        };
    }

    /**
     * Hanlder para quando um dos controles é clicado
     */
    public onButtonControlClicked(emmitedValue: number) {
        const max = Math.round(((this._value * 100) + (emmitedValue * 100)) / 100) > Math.round((this._max * 100) / 100) ;
        const min = Math.round(((this._value * 100) + (emmitedValue * 100)) / 100) < Math.round((this._min * 100) / 100) ;

        // caso esteja fora do  intervalo definido
        if (max || min) {
            this.onValueChange.emit(false);
            return;
        }

        if (this._symbol && this._parentInstrument) {
            
            if (this.isFractionary()) {
                this._max = this._parentInstrument.standardLot - 1;

                if ((this.value + emmitedValue) > this._max) {
                    this.value = this._max;
                    this.onValueChange.emit(true);
                    return;
                } 
            }              
        }
        
        this.value += emmitedValue;
        this.onValueChange.emit(true);
    }

    private isFractionary() {
        return (this._symbol.code.endsWith('F') || this._symbol.typeMarket === OrderMarketTypeEnum.Fractionary) ||
            (this._symbol.typeMarket === OrderMarketTypeEnum.ETFFractionary && this._symbol.code.endsWith('F'))
    }

    /**
     * Atualiza os parâmetros dos controles com base no valor fonecido
     * @param value valor para um lote
     */
    private updateControlsValuesParameteres(value: number) {
        value = Math.abs(value);

        if (this.type === 'default') {
            this._negativeMaxValue = - value;
            this._negativeIntermediaryValue = -value * 5;
            this._negativeMinValue = -value * 10;

            this._positiveMinValue = value;
            this._positiveIntermediaryValue = value * 5;
            this._positiveMaxValue = value * 10;
            return;
        }

        if (this.type === 'gain' || this.type === 'stop') {
            this._negativeMaxValue = -value;
            this._negativeIntermediaryValue = -value * 5;
            this._negativeMinValue = -value * 10;

            this._positiveMinValue =  value;
            this._positiveIntermediaryValue = value * 5;
            this._positiveMaxValue = value * 10;
            return;
        }
    }

    /**
     * Finaliza a edição
     */
    private stopEditing(event: Event) {
        if (this.type === 'default' && !this.enableEditingControls) return;

        event.stopPropagation();

        if (this.locked) return;

        this.locked = true;
        this.onEditingEnd.emit(true);
    }

    /**
     * Inicia a edição
     */
    private startEditing(event: Event) {
        this.onEditingRequest.emit(this.getCurrentComponentState());
    }
}
